import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { AnimatePresence, motion } from 'framer-motion';
import { GiHamburgerMenu } from 'react-icons/gi';
import { IoClose } from 'react-icons/io5';
import { FiArrowUpRight } from 'react-icons/fi';

const links = [
  { label: 'Home', to: '/', type: 'nav' },
  { label: 'About', to: '/about', type: 'nav' },
  { label: 'Services', to: '/#services', type: 'hash' },
  { label: 'Product', to: '/#product', type: 'hash' },
  { label: 'Contact', to: '/contact', type: 'nav' },
];

const Logo = () => (
  <NavLink to="/" className="flex items-center gap-3">
    <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-xl bg-ink shadow-mark">
      <img src="/logo512.png" alt="Lotez" className="h-7 w-7 object-contain" />
    </span>
    <span className="font-display text-lg font-bold tracking-tight text-ink">
      Lotez
    </span>
  </NavLink>
);

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? 'text-ink' : 'text-muted hover:text-ink'
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? 'border-b border-line bg-surface/80 backdrop-blur-xl'
            : 'border-b border-transparent bg-transparent'
        }`}
      >
        <nav className="container-x flex items-center justify-between py-4">
          <Logo />

          <ul className="hidden items-center gap-9 md:flex">
            {links.map((l) => (
              <li key={l.label}>
                {l.type === 'hash' ? (
                  <HashLink
                    to={l.to}
                    scroll={(el) =>
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    className="text-sm font-medium text-muted transition-colors hover:text-ink"
                  >
                    {l.label}
                  </HashLink>
                ) : (
                  <NavLink to={l.to} end={l.to === '/'} className={linkClass}>
                    {l.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>

          <NavLink to="/contact" className="btn-dark hidden md:inline-flex">
            Let's talk <FiArrowUpRight className="text-base" />
          </NavLink>

          <button
            type="button"
            aria-label="Toggle menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-white/60 text-ink md:hidden"
            onClick={() => setIsMenuOpen((s) => !s)}
          >
            {isMenuOpen ? (
              <IoClose className="text-xl" />
            ) : (
              <GiHamburgerMenu className="text-lg" />
            )}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden"
          >
            <div className="container-x pb-4">
              <div className="glass rounded-3xl p-4">
                <ul className="flex flex-col">
                  {links.map((l) => (
                    <li
                      key={l.label}
                      className="border-b border-ink/5 last:border-0"
                    >
                      {l.type === 'hash' ? (
                        <HashLink
                          to={l.to}
                          scroll={(el) =>
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                          className="block px-2 py-3 text-base font-medium text-muted hover:text-ink"
                        >
                          {l.label}
                        </HashLink>
                      ) : (
                        <NavLink
                          to={l.to}
                          end={l.to === '/'}
                          className="block px-2 py-3 text-base font-medium text-ink"
                        >
                          {l.label}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
                <NavLink to="/contact" className="btn-primary mt-3 w-full">
                  Let's talk <FiArrowUpRight className="text-base" />
                </NavLink>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
